import { Link } from 'react-router-dom';
import { Shield, ArrowLeft, Mail } from 'lucide-react';
import AppFooter from '../components/AppFooter';

const LAST_UPDATED = 'April 18, 2026';
const CONTACT_EMAIL = 'privacy@selahandstonelegacy.com';

export default function TermsOfServicePage() {
  return (
    <div className="min-h-screen bg-[#F5F5F5]" data-testid="terms-page">
      <header className="glass-header sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center" data-testid="home-link">
              <Shield className="w-8 h-8 text-[#B76E79]" strokeWidth={1.5} />
              <span className="ml-2 font-['Playfair_Display'] text-xl font-semibold text-[#36454F]">
                Selah Slate&trade;
              </span>
            </Link>
            <Link
              to="/dashboard"
              className="text-sm text-[#36454F]/70 hover:text-[#36454F] font-['Manrope'] flex items-center"
              data-testid="back-link"
            >
              <ArrowLeft className="w-4 h-4 mr-1" strokeWidth={1.5} />
              Back
            </Link>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="mb-8 animate-fade-in-up">
          <p className="text-xs uppercase tracking-wider text-[#B76E79] font-['Manrope'] font-medium mb-2">
            Legal
          </p>
          <h1 className="font-['Playfair_Display'] text-4xl font-semibold text-[#36454F]">
            Terms and Conditions
          </h1>
          <p className="mt-2 text-sm text-[#36454F]/60 font-['Manrope']">
            Last updated: {LAST_UPDATED}
          </p>
        </div>

        <article className="bg-white border border-[#36454F]/10 rounded-sm p-6 sm:p-10 space-y-8 font-['Manrope'] text-[#36454F]/85 leading-relaxed text-sm">
          <Section title="1. Who We Are">
            Selah Slate™ is a faith-based financial wellness tool provided free of charge by
            Selah and Stone Legacy Holdings LLC ("we," "us"). We are <strong>not</strong> a
            credit repair organization, law firm, debt collector, or financial advisor. By using
            the Service, or by checking the "I accept" box at signup, you acknowledge this and
            agree to these Terms and Conditions.
          </Section>

          <Section title="2. What We Provide">
            <ul className="list-disc pl-6 space-y-2">
              <li>Educational tools to help you understand your credit report.</li>
              <li>Automated generation of FCRA Section 611 dispute letter PDFs, Method of Verification (MOV) letters, and CFPB-complaint guidance.</li>
              <li>Tracking of mailed disputes and bureau responses you log.</li>
              <li>Faith-based content: devotionals, prayers, and scripture encouragement.</li>
              <li>Optional features: progress journal, debt-freedom calculator, testimony wall, and an anonymized ministry dashboard.</li>
            </ul>
          </Section>

          <Section title="3. No Legal or Financial Advice">
            <p>
              The Service is informational only and does not constitute legal, financial, or
              credit-repair advice. We do not guarantee any particular credit-score improvement,
              removal of any item, or response from any credit bureau. Bureau responses and
              outcomes are outside our control. For legal advice, consult a licensed attorney.
            </p>
          </Section>

          <Section title="4. Your Account & Accuracy">
            <ul className="list-disc pl-6 space-y-2">
              <li>You are at least 18 years old.</li>
              <li>The information you enter (name, address, SSN digits, items disputed) is truthful and belongs to you.</li>
              <li>You are solely responsible for mailing the letters, paying for postage / certified mail, and communicating with the bureaus.</li>
              <li>You are responsible for keeping your account credentials secure.</li>
            </ul>
          </Section>

          <Section title="5. Acceptable Use">
            <p>You agree not to:</p>
            <ul className="list-disc pl-6 space-y-2 mt-2">
              <li>Submit false, forged, or fraudulent information.</li>
              <li>Use the Service for another person without their consent.</li>
              <li>Attempt to probe, scan, or disrupt our systems.</li>
              <li>Reverse-engineer, scrape, or resell any part of the Service.</li>
              <li>Use the Service to abuse, threaten, or defame bureaus or creditors.</li>
            </ul>
            <p className="mt-3">
              We may suspend or terminate accounts that violate these rules.
            </p>
          </Section>

          <Section title="6. Free Service, No Purchases">
            The Service is free. We do not sell subscriptions, we do not offer in-app purchases,
            and we do not display advertising. If this ever changes, we will update these Terms
            and provide at least 30 days' notice.
          </Section>

          <Section title="7. Your Content">
            You retain ownership of anything you create on the Service (journal entries,
            testimonies, etc.). By posting a testimony to the public Testimony Wall, you grant
            us a limited, worldwide, royalty-free license to display, reproduce, and share it
            within the Service and in anonymized form elsewhere (e.g., our Ministry Dashboard).
            You can delete your testimonies at any time from your Profile.
          </Section>

          <Section title="8. Termination & Data Deletion">
            You may delete your account and all of your data at any time from{' '}
            <strong>Settings → Delete Account</strong>. Once confirmed, the deletion is
            permanent. We may also terminate or suspend accounts that violate these Terms or
            applicable law.
          </Section>

          <Section title="9. Third-Party Services">
            The Service integrates with OpenAI (AI analysis), Resend (email), MongoDB (hosting),
            and Google Sign-In (optional auth). Each third party has its own terms and privacy
            practices, which you should review.
          </Section>

          <Section title="10. Disclaimers & Limitation of Liability">
            <p>
              THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE." TO THE MAXIMUM EXTENT
              PERMITTED BY LAW, WE DISCLAIM ALL WARRANTIES, EXPRESS OR IMPLIED. WE ARE NOT
              LIABLE FOR ANY INDIRECT, INCIDENTAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, OR FOR
              ANY LOSS OF CREDIT OPPORTUNITY, PROFITS, OR DATA, ARISING OUT OF YOUR USE OF THE
              SERVICE. OUR AGGREGATE LIABILITY FOR ANY CLAIM WILL NOT EXCEED ONE HUNDRED U.S.
              DOLLARS ($100).
            </p>
          </Section>

          <Section title="11. Governing Law & Disputes">
            <p>
              These Terms are governed by the laws of the State of Florida, without regard to
              its conflict-of-law rules. Any dispute will be resolved in the state or federal
              courts located in Hillsborough County, Florida. You and we each waive the right
              to a jury trial.
            </p>
          </Section>

          <Section title="12. Changes to the Terms">
            <p>
              We may update these Terms and Conditions from time to time. For material changes
              we will notify you by email or in-app banner at least 14 days before they take
              effect. Your continued use after the effective date means you accept the updated
              Terms.
            </p>
          </Section>

          <Section title="13. Intellectual Property & Trademarks">
            <p>
              Selah Downloads&trade;, Selah Slate&trade;, Selah Rest&trade;, and Selah &amp; Salt
              Home&trade; are proprietary trademarks of Selah &amp; Stone Legacy Holdings LLC.
              All content, software, design, and branding within the Service are the exclusive
              property of Selah &amp; Stone Legacy Holdings LLC. All rights reserved.
              Unauthorized reproduction, distribution, or reverse engineering of this software
              codebase is strictly prohibited.
            </p>
          </Section>

          <Section title="14. Contact">
            <p className="flex items-center gap-2">
              <Mail className="w-4 h-4 text-[#B76E79]" strokeWidth={1.5} />
              <a className="text-[#B76E79] underline" href={`mailto:${CONTACT_EMAIL}`}>
                {CONTACT_EMAIL}
              </a>
            </p>
          </Section>
        </article>

        <p className="text-xs text-[#36454F]/45 font-['Manrope'] text-center mt-6">
          See also our{' '}
          <Link to="/privacy" className="text-[#B76E79] hover:underline" data-testid="privacy-link">
            Privacy Policy
          </Link>
          .
        </p>
      </main>

      <AppFooter />
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section>
      <h2 className="font-['Playfair_Display'] text-xl font-semibold text-[#36454F] mb-3">
        {title}
      </h2>
      <div className="space-y-2">{children}</div>
    </section>
  );
}
